/**
 * E06-F01-T05 — New Research Request (Intake) form. Composes every section of the
 * recruiter's criteria form and submits it via research_create (intakeApi).
 *
 * Sections (backlog.md#research-intake-форма-запроса):
 *   - Client        — name / website / about + "Add client to Do-Not-Source"
 *                     checkbox (checked by default).
 *   - Targeting     — Industry (datalist autocomplete), founded From/To window,
 *                     Sub-categories chip-input (datalist), Locations chip-input
 *                     (datalist), metro radius (mi/km, optional / city-only).
 *                     E06-F01-T10: locations come in STAGES ("Europe first, then
 *                     Global") + request-wide location exclusions ("excl. UK").
 *   - Size & type   — sizeGlobal band, local headcount cap, company-type +
 *                     funding pills with "+custom…" (delegated to T07's
 *                     VerifyStrictnessSection which also owns the Verification toggle).
 *   - Reference & exclusions — competitors / DNS companies / DNS industries chips,
 *                     + Avoid (E06-F01-T10, the brief's business models not to source).
 *   - Grades / purpose      — T06's PurposeGradeScopeSection.
 *   - Brief         — freeText + LinkedIn url.
 *   - targetCount slider (20..200, live value).
 *
 * E06-F06-T04 — under Targeting, the «Similar lists» hint (SimilarListsPanel): past
 * lists similar to the typed criteria, each openable (`onOpenSimilar`) or usable as a
 * criteria source («Use criteria» → the existing Clone; a non-blank form asks first).
 *
 * Chip fields reuse the shared ChipInput / DictionaryChipInput (datalist-backed);
 * the toggles/pills reuse the T06/T07 sections. State is one IntakeCriteria object
 * (emptyIntakeCriteria default); submit posts it.
 */
import { useEffect, useRef, useState, type JSX } from 'react';
import {
  LOCATION_STAGES_MAX,
  TARGET_COUNT_MAX,
  TARGET_COUNT_MIN,
  conflictingDraftId,
  type IntakeCriteria,
  type SimilarListRow,
} from '@eer/shared';
import { ChipInput } from '../ui/ChipInput.js';
import { ApiResultError, errorMessage } from '../api/client.js';
import { useToast } from '../ui/ToastContext.js';
import { useConfirm } from '../ui/ConfirmDialog.js';
import { useProjects } from '../project/useProjects.js';
import { useClientsCache } from '../project/projectDetailApi.js';
import { ClientAutocomplete } from '../project/ClientAutocomplete.js';
import {
  DictionaryDatalists,
  DictionaryChipInput,
  IndustryAutocompleteInput,
} from './IntakeAutocomplete.js';
import { PurposeGradeScopeSection } from './PurposeGradeScopeSection.js';
import { VerifyStrictnessSection } from './VerifyStrictnessSection.js';
import { SizeWindowInput } from './SizeWindowInput.js';
import { ProjectAutocomplete } from './ProjectAutocomplete.js';
import { ResearchProjectPeople } from './ResearchProjectPeople.js';
import { SimilarListsPanel } from './SimilarListsPanel.js';
import { useCloneResearchRequest } from './researchLibraryApi.js';
import {
  emptyIntakeCriteria,
  INTAKE_COST_ESTIMATE,
  applyNormalizeResult,
  applyPastedBrief,
  isIntakeCriteriaBlank,
  parsePastedBrief,
  locationStageCaption,
  locationStageCount,
  removeLocationStage,
  setStageLocations,
  stageLocationLabels,
} from './intakeModel.js';
import {
  useResearchCreate,
  useResearchUpdateDraft,
  useCriteriaNormalize,
  useGlobalDnsCount,
} from './intakeApi.js';
import type { IntakeDraftSnapshot } from './researchNavModel.js';

/** E06-F01-T14 — research cannot be requested without a linked project. */
export const PROJECT_REQUIRED_MESSAGE =
  'Link the request to a project — research cannot be requested without a project';

export interface IntakeFormProps {
  /** Optional initial criteria (e.g. resuming a draft or a History clone). */
  readonly initialCriteria?: IntakeCriteria;
  /** Optional project to link the request to (seeds the Link-to-project select). */
  readonly projectId?: number | null;
  /** Fired with the created request id on a "Run research" submit (starts the run). */
  readonly onCreated?: (requestId: number) => void;
  /**
   * Fired with the created request id on a "Save draft" submit (does NOT start a
   * run). Optional — when omitted Save-draft just persists + toasts success.
   */
  readonly onDraftSaved?: (requestId: number) => void;
  /** E06-F02-T12 — initial Request title (restoring the form on "Back"). */
  readonly initialTitle?: string;
  /**
   * E06-F02-T12 — reports what the form currently holds (criteria / title / linked
   * project) on mount and on every change, so the Research host can keep it with
   * the Intake history entry and "Back" to the form never loses the typed request.
   */
  readonly onDraftChange?: (draft: IntakeDraftSnapshot) => void;
  /**
   * E06-F06-T04 — the request this form edits (a clone / an edit of an existing one);
   * excluded from the «Similar lists» hint. Omitted / 0 for a brand-new request.
   */
  readonly editingRequestId?: number | null;
  /** E06-F06-T04 — «Open» on a similar list: navigate to that request. */
  readonly onOpenSimilar?: (row: SimilarListRow) => void;
  /**
   * E12-F17-T07 — the saved DRAFT this form edits (History «Open → Request»). When
   * set, Save draft / Run update THAT row (PATCH) instead of creating a new one, and
   * Run starts it. Omitted / 0 for a new request or a clone (those create a row).
   */
  readonly draftRequestId?: number | null;
  /**
   * E12-F17-T07 — open an existing draft (the 409 «A draft with identical criteria
   * already exists» names one); the form shows an «Open the existing draft» link.
   */
  readonly onOpenDraft?: (requestId: number) => void;
}

/** A labelled section card. */
function Section({
  title,
  children,
  testId,
}: {
  title: string;
  children: JSX.Element | JSX.Element[];
  testId: string;
}): JSX.Element {
  return (
    <section
      data-testid={testId}
      className="rounded-lg border border-[var(--color-border)] bg-white p-4 flex flex-col gap-3"
    >
      <h3 className="text-sm font-semibold text-[var(--color-text-strong)]">{title}</h3>
      {children}
    </section>
  );
}

export function IntakeForm({
  initialCriteria,
  projectId = null,
  onCreated,
  onDraftSaved,
  initialTitle,
  onDraftChange,
  editingRequestId = null,
  onOpenSimilar,
  draftRequestId = null,
  onOpenDraft,
}: IntakeFormProps): JSX.Element {
  const toast = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const clone = useCloneResearchRequest();
  // E06-F06-T04 — the similar list whose criteria are being taken (button busy).
  const [takingId, setTakingId] = useState<number | null>(null);
  const [criteria, setCriteria] = useState<IntakeCriteria>(
    initialCriteria ?? emptyIntakeCriteria(),
  );
  const [title, setTitle] = useState(initialTitle ?? '');
  // Search radius is a single UI value applied to city locations (optional, km).
  const [metroRadius, setMetroRadius] = useState<number | null>(null);
  // E06-F01-T10 — stage rows shown (an added stage stays visible while still empty).
  const [stageRows, setStageRows] = useState<number>(() =>
    locationStageCount((initialCriteria ?? emptyIntakeCriteria()).locations),
  );
  const stagesShown = Math.max(stageRows, locationStageCount(criteria.locations));
  // In-form Link-to-project select (prototype intake "Link to project"); seeded
  // from the prop (a cloned draft may carry its source's linked project).
  const [linkedProjectId, setLinkedProjectId] = useState<number | null>(projectId);
  // E06-F01-T14 — inline "link a project" error, shown after a blocked submit.
  const [projectError, setProjectError] = useState<string | null>(null);
  // The AI paste-to-parse accelerator textarea.
  const [pasteText, setPasteText] = useState('');
  // E06-F02-T12 — mirror the form's values up to the host (latest callback via ref,
  // so a new callback identity alone never re-fires the report).
  const onDraftChangeRef = useRef(onDraftChange);
  onDraftChangeRef.current = onDraftChange;
  useEffect(() => {
    onDraftChangeRef.current?.({ criteria, title, projectId: linkedProjectId });
  }, [criteria, title, linkedProjectId]);
  const create = useResearchCreate();
  const updateDraft = useResearchUpdateDraft();
  const saving = create.isPending || updateDraft.isPending;
  // E12-F17-T07 — the existing draft a 409 «identical criteria» pointed at.
  const [conflictDraft, setConflictDraft] = useState<number | null>(null);
  const editsDraft = typeof draftRequestId === 'number' && draftRequestId > 0;
  const globalDnsCount = useGlobalDnsCount();
  const normalize = useCriteriaNormalize();
  const projectsQuery = useProjects();
  const clientsQuery = useClientsCache();

  /** Patch helper — shallow-merge into the criteria object. */
  function patch(partial: Partial<IntakeCriteria>): void {
    setCriteria((c) => ({ ...c, ...partial }));
  }

  /**
   * Paste-to-parse accelerator: sends the pasted brief to the real
   * `research_criteria_normalize` AI endpoint and merges the structured result
   * onto the form. Posts to the id-less `POST /research/requests/normalize`
   * (E06-F01-T05), so the PRIMARY flow — pasting a raw brief onto an EMPTY form
   * — works without first minting a throwaway draft request (creating one needs
   * a non-empty industry+location, which an empty form cannot yet provide). On a
   * genuine failure the real backend message is surfaced, not a generic swallow.
   *
   * E06-F01-T09 — TWO passes, both non-clobbering:
   *   1. the DETERMINISTIC one ({@link parsePastedBrief}) runs locally FIRST, so a
   *      labelled brief ("Industry: …", "Location: …") fills the form even when
   *      the AI call later fails, and the already-known fields travel to the model
   *      as `rawIntake`. Free-form prose falls through it into `freeText` (the
   *      Brief field), so the pasted request is never lost;
   *   2. the AI result is merged ON TOP ({@link applyNormalizeResult}) — it now
   *      carries industry / sub-categories / role / client / size / types, which
   *      is the actual bug fix (before T09 only the location ever came back).
   * The recruited ROLE has no field of its own in the intake, so it seeds the
   * Request title when the recruiter left that blank.
   */
  async function onParsePaste(): Promise<void> {
    if (!pasteText.trim()) {
      toast.error('Paste a request first');
      return;
    }
    // Pass 1 — local, synchronous, cannot fail.
    const parsed = parsePastedBrief(pasteText);
    const seeded = parsed ? applyPastedBrief(criteria, parsed) : criteria;
    setCriteria(seeded);
    try {
      const res = await normalize.mutateAsync({
        input: { rawText: pasteText, rawIntake: seeded },
      });
      if (res.result) {
        // Pass 2 — AI on top of what pass 1 (and the recruiter) already filled.
        setCriteria(applyNormalizeResult(seeded, res.result));
        const role = (res.result.roleTitle ?? '').trim();
        if (role && !title.trim()) setTitle(role);
        toast.success('Parsed the request — fields autofilled');
      } else {
        toast.error(res.warning ?? 'Could not parse the request');
      }
    } catch (e) {
      toast.error(errorMessage(e, 'Failed to parse the request'));
    }
  }

  /**
   * E06-F06-T04 — «Use criteria» on a similar list: fill the form with that list's
   * criteria through the existing Clone (POST /research/list/clone — the same path as
   * History «Clone»; read-only since E12-F17-T04, so nothing lands in History). A non-blank form is overwritten only after a confirmation. The
   * title and the linked project stay as the recruiter set them.
   */
  async function onTakeSimilarCriteria(row: SimilarListRow): Promise<void> {
    if (takingId != null) return;
    if (!isIntakeCriteriaBlank(criteria)) {
      const ok = await confirm({
        title: 'Use criteria of a similar list',
        message: `Replace the criteria in this form with the criteria of "${row.title || `#${row.requestId}`}"?`,
        confirmLabel: 'Replace',
        testId: 'similar-take-confirm',
      });
      if (!ok) return;
    }
    setTakingId(row.requestId);
    try {
      const res = await clone.mutateAsync(row.requestId);
      const next = res.prefill.criteria;
      setCriteria(next);
      setStageRows(locationStageCount(next.locations));
      toast.success('Criteria filled from the similar list');
    } catch (e) {
      toast.error(errorMessage(e, 'Could not load the criteria of that list'));
    } finally {
      setTakingId(null);
    }
  }

  /**
   * The criteria to SEND (PK-QA-12 save boundary). The Client field is a controlled
   * `ClientAutocomplete` whose live value carries the user's RAW typed text (spaces
   * preserved so multi-word names can be typed), so trim `clientName` here — at the
   * submit boundary — rather than per-keystroke, so an untrimmed name never reaches
   * the api.
   */
  function criteriaForSubmit(): IntakeCriteria {
    return { ...criteria, clientName: criteria.clientName.trim() };
  }

  /** Shared validation for both Run and Save-draft submits. */
  function validate(): boolean {
    // E06-F01-T14 — no research without a linked project (blocked at the request
    // stage; the api enforces the same rule with a 400).
    if (typeof linkedProjectId !== 'number' || linkedProjectId <= 0) {
      setProjectError(PROJECT_REQUIRED_MESSAGE);
      toast.error(PROJECT_REQUIRED_MESSAGE);
      return false;
    }
    if (!criteria.industry.trim()) {
      toast.error('Industry is required');
      return false;
    }
    if (criteria.locations.length === 0) {
      toast.error('At least one location is required');
      return false;
    }
    // E06-F01-T10 — a later stage cannot stand without a first one.
    if (stageLocationLabels(criteria.locations, 1).length === 0) {
      toast.error('Stage 1 needs at least one location');
      return false;
    }
    return true;
  }

  /**
   * Persist the form: an opened draft (E12-F17-T07) is UPDATED in place — never a
   * second row, never a blanked one; otherwise a new draft is created. Returns the
   * request id to run / report.
   */
  async function persist(): Promise<number> {
    const payload = {
      projectId: linkedProjectId ?? null,
      title,
      criteria: criteriaForSubmit(),
    };
    if (editsDraft) {
      const res = await updateDraft.mutateAsync({ requestId: draftRequestId, input: payload });
      return res.request.id;
    }
    const res = await create.mutateAsync({ ...payload, forceNew: false });
    return res.request.id;
  }

  /**
   * A failed submit: toast the message and, on the 409 «A draft with identical
   * criteria already exists», remember that draft so the form offers to open it
   * (E12-F17-T07 — no more dead end).
   */
  function onSubmitError(e: unknown, fallback: string): void {
    const existing =
      e instanceof ApiResultError && e.detail.code === 'CONFLICT'
        ? conflictingDraftId(e.detail.details)
        : null;
    setConflictDraft(existing !== null && existing !== draftRequestId ? existing : null);
    toast.error(errorMessage(e, fallback));
  }

  /** "Run research" — persist the request, then hand the id up to start the run. */
  async function onSubmit(): Promise<void> {
    if (!validate()) return;
    setConflictDraft(null);
    try {
      const id = await persist();
      toast.success('Research request created');
      onCreated?.(id);
    } catch (e) {
      onSubmitError(e, 'Failed to create research request');
    }
  }

  /** "Save draft" — persist as a draft WITHOUT starting a run. */
  async function onSaveDraft(): Promise<void> {
    if (!validate()) return;
    setConflictDraft(null);
    try {
      const id = await persist();
      toast.success('Draft saved');
      onDraftSaved?.(id);
    } catch (e) {
      onSubmitError(e, 'Failed to save the draft');
    }
  }

  return (
    <form
      data-testid="intake-form"
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmit();
      }}
    >
      <DictionaryDatalists />
      {confirmDialog}

      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium text-[var(--color-text-strong)]">Request title</span>
        <input
          type="text"
          data-testid="intake-title"
          className="border border-[var(--color-border)] rounded-md p-2 text-sm outline-none focus:border-[var(--color-accent)]"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Senior Backend — fintech, EU"
        />
      </label>

      {/* --- AI paste-to-parse accelerator (T05) --- */}
      <div
        data-testid="intake-paste-parse"
        className="rounded-lg border border-dashed border-[var(--color-accent)] bg-amber-50/40 p-4 flex flex-col gap-2"
      >
        <label className="flex items-center gap-2 text-xs font-bold text-[var(--color-text-strong)]">
          <i className="fa-solid fa-wand-magic-sparkles text-[var(--color-accent)]" aria-hidden />
          Paste the recruiter&apos;s request — autofill the fields below
        </label>
        <textarea
          data-testid="intake-paste-text"
          className="w-full border border-[var(--color-border)] rounded-md p-2 text-xs font-mono bg-white outline-none focus:border-[var(--color-accent)]"
          rows={2}
          value={pasteText}
          onChange={(e) => setPasteText(e.target.value)}
          placeholder="Paste the email / Word request (Client, Industry, Sub Category, Location, Size, Company Type, Grade A/B, DNS, Notes…)"
        />
        <div className="flex justify-end">
          <button
            type="button"
            data-testid="intake-paste-parse-btn"
            disabled={normalize.isPending}
            onClick={() => void onParsePaste()}
            className="min-h-[36px] px-3 rounded-md bg-slate-900 text-white text-xs font-bold hover:bg-slate-700"
          >
            <i className="fa-solid fa-bolt mr-1" aria-hidden /> Parse &amp; autofill
          </button>
        </div>
      </div>

      {/* --- Client --- */}
      <Section title="Client" testId="intake-section-client">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-[var(--color-text-strong)]">Name</span>
          <ClientAutocomplete
            value={criteria.clientName}
            clients={clientsQuery.data ?? []}
            onResolve={(r) => patch({ clientName: r.client_name, clientId: r.client_id })}
            testId="intake-client-name"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-[var(--color-text-strong)]">Website</span>
          <input
            type="text"
            data-testid="intake-client-website"
            className="border border-[var(--color-border)] rounded-md p-2 text-sm outline-none focus:border-[var(--color-accent)]"
            value={criteria.clientWebsite}
            onChange={(e) => patch({ clientWebsite: e.target.value })}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-[var(--color-text-strong)]">About</span>
          <textarea
            data-testid="intake-client-about"
            className="border border-[var(--color-border)] rounded-md p-2 text-sm min-h-[60px] outline-none focus:border-[var(--color-accent)]"
            value={criteria.clientAbout}
            onChange={(e) => patch({ clientAbout: e.target.value })}
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            data-testid="intake-add-to-dns"
            checked={criteria.addClientToDns}
            onChange={(e) => patch({ addClientToDns: e.target.checked })}
          />
          <span>Add client to Do-Not-Source</span>
        </label>
      </Section>

      {/* --- Targeting --- */}
      <Section title="Targeting" testId="intake-section-targeting">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-[var(--color-text-strong)]">
            Industry <span className="text-[var(--color-danger)]">*</span>
          </span>
          <IndustryAutocompleteInput
            value={criteria.industry}
            onChange={(v) => patch({ industry: v })}
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-[var(--color-text-strong)]">Founded from</span>
            <input
              type="number"
              data-testid="intake-founded-from"
              className="border border-[var(--color-border)] rounded-md p-2 text-sm outline-none focus:border-[var(--color-accent)]"
              value={criteria.foundedFrom ?? ''}
              onChange={(e) =>
                patch({ foundedFrom: e.target.value ? Number(e.target.value) : null })
              }
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-[var(--color-text-strong)]">Founded to</span>
            <input
              type="number"
              data-testid="intake-founded-to"
              className="border border-[var(--color-border)] rounded-md p-2 text-sm outline-none focus:border-[var(--color-accent)]"
              value={criteria.foundedTo ?? ''}
              onChange={(e) => patch({ foundedTo: e.target.value ? Number(e.target.value) : null })}
            />
          </label>
        </div>

        <div className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-[var(--color-text-strong)]">Sub-categories</span>
          <DictionaryChipInput
            dictKey="sub_category"
            value={criteria.subCategories}
            onChange={(chips) => patch({ subCategories: chips })}
            placeholder="Add sub-category, Enter to add"
          />
        </div>

        {/* E06-F01-T10 — locations by stage: stage 1 is searched first, a later
            stage only when the earlier ones did not reach the target count. */}
        <div className="flex flex-col gap-2 text-sm" data-testid="intake-location-stages">
          <span className="font-medium text-[var(--color-text-strong)]">
            Locations <span className="text-[var(--color-danger)]">*</span>
          </span>
          {Array.from({ length: stagesShown }, (_, i) => i + 1).map((stage) => (
            <div
              key={stage}
              className="flex flex-col gap-1"
              data-testid={`intake-location-stage-${stage}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-[var(--color-text-muted)]">
                  {locationStageCaption(stage)}
                </span>
                {stage > 1 && (
                  <button
                    type="button"
                    data-testid={`intake-location-stage-${stage}-remove`}
                    className="text-xs text-[var(--color-danger)] hover:underline"
                    onClick={() => {
                      patch({ locations: removeLocationStage(criteria.locations, stage) });
                      setStageRows((n) => Math.max(1, n - 1));
                    }}
                  >
                    Remove stage
                  </button>
                )}
              </div>
              <DictionaryChipInput
                dictKey="location"
                // Stage 1 keeps the pre-T10 testid (intake-location-*).
                testId={stage === 1 ? 'intake-location' : `intake-location-s${stage}`}
                value={stageLocationLabels(criteria.locations, stage)}
                onChange={(labels) =>
                  patch({
                    locations: setStageLocations(criteria.locations, stage, labels, metroRadius),
                  })
                }
                placeholder={
                  stage === 1
                    ? 'Add region / country, Enter to add'
                    : 'Then search here, e.g. Global — Enter to add'
                }
              />
            </div>
          ))}
          {stagesShown < LOCATION_STAGES_MAX && (
            <button
              type="button"
              data-testid="intake-location-add-stage"
              className="self-start text-xs font-medium text-[var(--color-accent)] hover:underline"
              onClick={() => setStageRows(stagesShown + 1)}
            >
              + Add next stage (e.g. "then Global")
            </button>
          )}
        </div>

        <div className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-[var(--color-text-strong)]">
            Excluding locations (all stages)
          </span>
          <ChipInput
            value={criteria.locationExclusions}
            onChange={(chips) => patch({ locationExclusions: chips })}
            placeholder="e.g. UK — Enter to add"
            testId="intake-location-exclusions"
          />
          <span className="text-xs text-[var(--color-text-muted)]">
            Countries or regions never searched, in any stage (&quot;Global excl. UK&quot;).
          </span>
        </div>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-[var(--color-text-strong)]">
            Search radius, km (optional, city only)
          </span>
          <input
            type="number"
            data-testid="intake-search-radius-km"
            className="border border-[var(--color-border)] rounded-md p-2 text-sm outline-none focus:border-[var(--color-accent)]"
            value={metroRadius ?? ''}
            onChange={(e) => {
              const r = e.target.value ? Number(e.target.value) : null;
              setMetroRadius(r);
              patch({ locations: criteria.locations.map((l) => ({ ...l, radiusKm: r })) });
            }}
          />
        </label>
      </Section>

      {/* --- Similar lists hint (E06-F06-T04) — renders nothing until Targeting is
          filled, and nothing on a failed request (never blocks the form). --- */}
      <SimilarListsPanel
        criteria={criteria}
        excludeRequestId={editingRequestId}
        onOpen={onOpenSimilar}
        onTakeCriteria={(row) => void onTakeSimilarCriteria(row)}
        takingRequestId={takingId}
      />

      {/* --- Size & type (+ Verification + company-type/funding pills, T07) --- */}
      <Section title="Size & type" testId="intake-section-size">
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-[var(--color-text-strong)]">
              Company size — global
            </span>
            {/* E07-F02-T19 rework — structured (mode + numbers), no free text. */}
            <SizeWindowInput
              value={criteria.sizeGlobal}
              onChange={(v) => patch({ sizeGlobal: v })}
            />
          </div>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-[var(--color-text-strong)]">Local headcount cap</span>
            <span className="text-xs text-[var(--color-text-muted)]">
              Optional: max staff of the local office/subsidiary — separate from the global size.
            </span>
            <input
              type="number"
              data-testid="intake-size-local-cap"
              className="border border-[var(--color-border)] rounded-md p-2 text-sm outline-none focus:border-[var(--color-accent)]"
              value={criteria.sizeLocalCap ?? ''}
              onChange={(e) =>
                patch({ sizeLocalCap: e.target.value ? Number(e.target.value) : null })
              }
            />
          </label>
        </div>
        <VerifyStrictnessSection
          verifyStrictness={criteria.verifyStrictness}
          onVerifyStrictnessChange={(v) => patch({ verifyStrictness: v })}
          companyTypes={criteria.companyTypes}
          onCompanyTypesChange={(next) => patch({ companyTypes: next })}
          fundingStages={criteria.fundingStages}
          onFundingStagesChange={(next) => patch({ fundingStages: next })}
        />
      </Section>

      {/* --- Reference & exclusions --- */}
      <Section title="Reference & exclusions" testId="intake-section-exclusions">
        <div className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-[var(--color-text-strong)]">
            Competitors (Grade-A seeds)
          </span>
          <ChipInput
            value={criteria.competitors}
            onChange={(chips) => patch({ competitors: chips })}
            placeholder="Add competitor, Enter to add"
            testId="intake-competitors"
          />
          <span className="text-xs text-[var(--color-text-muted)]">
            Tip: site-name preferred over a display name.
          </span>
        </div>
        <div className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-[var(--color-text-strong)]">
            Do-Not-Source companies
          </span>
          <ChipInput
            value={criteria.dnsCompanies}
            onChange={(chips) => patch({ dnsCompanies: chips })}
            placeholder="Add company, Enter to add"
            testId="intake-dns-companies"
          />
        </div>
        <div className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-[var(--color-text-strong)]">
            Do-Not-Source industries
          </span>
          <ChipInput
            value={criteria.dnsIndustries}
            onChange={(chips) => patch({ dnsIndustries: chips })}
            placeholder="Add industry, Enter to add"
            testId="intake-dns-industries"
          />
        </div>
        <div className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-[var(--color-text-strong)]">Avoid</span>
          <ChipInput
            value={criteria.avoid}
            onChange={(chips) => patch({ avoid: chips })}
            placeholder="e.g. distributors, pure SaaS — Enter to add"
            testId="intake-avoid"
          />
          <span className="text-xs text-[var(--color-text-muted)]">
            Business models not to source — used by the discovery filter and scoring.
          </span>
        </div>
        {/* E07-F07-T03 — the global Do-Not-Source list (EER clients); default ON. */}
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            data-testid="intake-use-global-dns"
            checked={criteria.useGlobalDns !== false}
            onChange={(e) => patch({ useGlobalDns: e.target.checked })}
          />
          <span data-testid="intake-use-global-dns-label">
            {typeof globalDnsCount.data === 'number'
              ? `Exclude EER clients (global list, ${globalDnsCount.data} ${
                  globalDnsCount.data === 1 ? 'company' : 'companies'
                })`
              : 'Exclude EER clients (global list)'}
          </span>
        </label>
      </Section>

      {/* --- Grades / purpose (T06) --- */}
      <Section title="Grades & purpose" testId="intake-section-grades">
        <PurposeGradeScopeSection
          purpose={criteria.purpose}
          onPurposeChange={(p) => patch({ purpose: p })}
          gradeScope={criteria.gradeScope}
          onGradeScopeChange={(s) => patch({ gradeScope: s })}
          gradeDefinitions={criteria.gradeDefinitions}
          onGradeDefinitionsChange={(g) => patch({ gradeDefinitions: g })}
        />
      </Section>

      {/* --- Brief --- */}
      <Section title="Brief" testId="intake-section-brief">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-[var(--color-text-strong)]">Free text</span>
          <textarea
            data-testid="intake-free-text"
            className="border border-[var(--color-border)] rounded-md p-2 text-sm min-h-[80px] outline-none focus:border-[var(--color-accent)]"
            value={criteria.freeText}
            onChange={(e) => patch({ freeText: e.target.value })}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-[var(--color-text-strong)]">LinkedIn URL</span>
          <input
            type="text"
            data-testid="intake-linkedin"
            className="border border-[var(--color-border)] rounded-md p-2 text-sm outline-none focus:border-[var(--color-accent)]"
            value={criteria.linkedinUrl}
            onChange={(e) => patch({ linkedinUrl: e.target.value })}
          />
        </label>
        <div className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-[var(--color-text-strong)]">
            Link to project{' '}
            <span className="text-red-600" data-testid="intake-project-required">
              *
            </span>
          </span>
          {/* E06-F01-T13: search-with-autocomplete (client / role / ID) instead of a flat select. */}
          <ProjectAutocomplete
            value={linkedProjectId}
            projects={Array.isArray(projectsQuery.data) ? projectsQuery.data : []}
            onChange={(id) => {
              setLinkedProjectId(id);
              if (typeof id === 'number' && id > 0) setProjectError(null);
            }}
            loading={projectsQuery.isLoading}
            required
          />
          {projectError ? (
            <p className="text-xs text-red-600" role="alert" data-testid="intake-project-error">
              {projectError}
            </p>
          ) : linkedProjectId == null ? (
            // E06-F01-T16 — flag the empty mandatory link up front, not only on submit.
            <p className="text-xs text-red-600" data-testid="intake-project-hint">
              Pick a project — research must be linked to one.
            </p>
          ) : null}
        </div>
        {/* E06-F01-T14 — the project's recruiter + team leads, once a project is linked. */}
        <ResearchProjectPeople projectId={linkedProjectId} testIdPrefix="intake" />
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-[var(--color-text-strong)]">
            Target count:{' '}
            <strong data-testid="intake-target-count-value">{criteria.targetCount}</strong>
          </span>
          <input
            type="range"
            data-testid="intake-target-count"
            min={TARGET_COUNT_MIN}
            max={TARGET_COUNT_MAX}
            value={criteria.targetCount}
            onChange={(e) => patch({ targetCount: Number(e.target.value) })}
          />
        </label>
      </Section>

      {/* E12-F17-T07 — the 409 «identical criteria» names an existing draft: offer it. */}
      {conflictDraft !== null ? (
        <div
          role="alert"
          data-testid="intake-conflict-draft"
          className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm"
        >
          <span>A draft with identical criteria already exists (#{conflictDraft}).</span>
          {onOpenDraft ? (
            <button
              type="button"
              data-testid="intake-conflict-open-draft"
              onClick={() => onOpenDraft(conflictDraft)}
              className="font-semibold text-[var(--color-primary)] hover:underline"
            >
              Open the existing draft
            </button>
          ) : null}
        </div>
      ) : null}

      {/* --- Cost / ETA footer + actions (T05) --- */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--color-border)] pt-4">
        <p data-testid="intake-cost-estimate" className="text-xs text-[var(--color-text-muted)]">
          <i className="fa-solid fa-circle-info mr-1 text-[var(--color-text-muted)]" aria-hidden />
          Estimated cost: <b>{INTAKE_COST_ESTIMATE.cold}</b> cold ·{' '}
          <b>{INTAKE_COST_ESTIMATE.warm}</b> warm (library cache) · {INTAKE_COST_ESTIMATE.eta}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="intake-save-draft"
            disabled={saving}
            onClick={() => void onSaveDraft()}
            className="min-h-[40px] px-4 rounded-md border border-[var(--color-border)] bg-white text-sm font-medium text-[var(--color-text-strong)] hover:bg-gray-50 disabled:opacity-60"
          >
            Save draft
          </button>
          <button
            type="submit"
            data-testid="intake-submit"
            disabled={saving}
            className="min-h-[40px] px-5 rounded-md bg-[var(--color-primary)] text-white text-sm font-semibold hover:bg-[var(--color-primary-hover)] disabled:opacity-60"
          >
            <i className="fa-solid fa-bolt mr-1" aria-hidden /> Run research
          </button>
        </div>
      </div>
    </form>
  );
}
